import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        const meta = session?.user?.raw_user_meta_data || session?.user?.user_metadata;
        const appMeta = session?.user?.app_metadata;
        if (meta?.role === 'admin' || appMeta?.role === 'admin') {
          navigate('/admin-dashboard');
        }
      }
    };
    checkSession();
  }, [navigate]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase?.auth?.signInWithPassword({ email, password });
      if (signInError) throw new Error(signInError.message);
      const user = data?.user;
      const meta = user?.user_metadata;
      const appMeta = user?.app_metadata;
      if (meta?.role !== 'admin' && appMeta?.role !== 'admin') {
        await supabase?.auth?.signOut();
        throw new Error('Access denied. Admin credentials required.');
      }
      navigate('/admin-dashboard');
    } catch (err) {
      setError(err?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location?.origin}/admin-login`,
      });
      if (resetError) throw new Error(resetError.message);
      setForgotSent(true);
    } catch (err) {
      setError(err?.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/assets/images/Logo_final_no_background-1771860991795-1772040132991.png"
            alt="StrategyShelf Logo"
            className="h-12 w-auto object-contain mx-auto mb-4"
          />
          <h1 className="text-2xl font-heading font-bold text-foreground">Admin Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">StrategyShelf Management System</p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-xl shadow-lg p-8">
          {!forgotMode ? (
            <>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-6">Sign In</h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email Address</label>
                  <div className="relative">
                    <Icon name="Mail" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      placeholder="admin@strategyshelf.com"
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <Icon name="Lock" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e?.target?.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e?.target?.checked)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"
                    />
                    <span className="text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setError(''); }}
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><Icon name="Loader2" size={16} className="animate-spin" /> Signing in...</>
                  ) : (
                    <><Icon name="LogIn" size={16} /> Sign In to Admin</>  
                  )}
                </button>
              </form>

              {/* Security badges */}
              <div className="mt-6 pt-5 border-t border-border">
                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Shield" size={12} />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Lock" size={12} />
                    <span>Admin Only Access</span>
                  </div>
                </div>
              </div>

              {/* Demo credentials */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground text-center font-medium mb-1">Demo Admin Credentials</p>
                <p className="text-xs text-muted-foreground text-center">admin@strategyshelf.com / Admin@2024!</p>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { setForgotMode(false); setError(''); setForgotSent(false); }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors"
              >
                <Icon name="ArrowLeft" size={14} /> Back to login
              </button>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Reset Password</h2>
              <p className="text-sm text-muted-foreground mb-6">Enter your admin email to receive a reset link.</p>

              {forgotSent ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                  <Icon name="CheckCircle" size={24} color="#22c55e" className="mx-auto mb-2" />
                  <p className="text-sm text-green-700 font-medium">Reset email sent!</p>
                  <p className="text-xs text-green-600 mt-1">Check your inbox for the password reset link.</p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Admin Email</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e?.target?.value)}
                      placeholder="admin@strategyshelf.com"
                      required
                      className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Icon name="Loader2" size={16} className="animate-spin" /> Sending...</> : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          &copy; {new Date()?.getFullYear()} StrategyShelf. Admin access only.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
